import { PaginationState } from '@tanstack/react-table';
import { NoddiIcon } from 'noddi-ui-common';
import { Button } from '../../../../base/button/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../base/select';
import { NoddiSpinner } from '../../../Feedback/NoddiSpinner';
import { resolveTablePaginationTranslations, TablePaginationTranslations } from '../tableTranslationTypes';

interface Props {
  pagination: PaginationState;
  totalCount: number | undefined;
  onChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  pageSizeOptions?: number[];
  selectedCount?: number;
  translations?: Partial<TablePaginationTranslations>;
  loading?: boolean;
}

export function TablePagination({
  pagination,
  totalCount,
  onChange,
  pageSizeOptions = [10, 15, 25, 50, 100],
  selectedCount,
  translations,
  loading
}: Props) {
  const { pageIndex, pageSize } = pagination;

  const texts = resolveTablePaginationTranslations(translations);

  // An undefined totalCount means the query has not resolved yet — showing a count
  // would falsely claim "Total of 0" while the data is still loading.
  const isCountUnknown = totalCount === undefined;
  const showLoading = loading || isCountUnknown;

  const pageCount = Math.ceil((totalCount ?? 0) / pageSize) || 1;
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  // Ensure the current pageSize is in the options, otherwise add it and sort
  const effectivePageSizeOptions = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <div className='flex flex-wrap-reverse items-center justify-between gap-x-6 gap-y-1 px-0.5 whitespace-nowrap lg:gap-x-6'>
      {/* hardcoding width here to prevent layout shift when loading */}
      <div className='min-w-[150px]'>
        {showLoading ? (
          <div className='flex items-start py-2'>
            <NoddiSpinner size='small' />
          </div>
        ) : (
          <div className='flex-1 text-muted-foreground'>
            {selectedCount !== undefined ? (
              <span>
                {selectedCount} {texts.of} {totalCount} {texts.row} {texts.selected}.
              </span>
            ) : (
              <span>
                {texts.totalOf} {totalCount} {texts.item}
              </span>
            )}
          </div>
        )}
      </div>
      <div className='flex flex-wrap items-center justify-between gap-x-6 gap-y-1 lg:gap-x-6'>
        <div className='flex items-center gap-2 lg:gap-4'>
          <p className='font-medium'>{texts.pageSize}</p>
          <Select value={`${pageSize}`} onValueChange={(value) => onChange({ pageIndex: 0, pageSize: Number(value) })}>
            <SelectTrigger className='h-8 w-[70px]'>
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent side='top'>
              {effectivePageSizeOptions.map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex gap-2 lg:gap-4'>
          <div className='flex items-center justify-center font-medium'>
            {texts.page} {pageIndex + 1} {texts.of} {isCountUnknown ? '...' : pageCount}
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              className='size-8 p-0'
              onClick={() => onChange({ pageIndex: 0, pageSize })}
              disabled={!canPreviousPage}
            >
              <span className='sr-only'>{texts.goToFirstPage}</span>
              <NoddiIcon name='ChevronsLeft' />
            </Button>
            <Button
              variant='outline'
              className='size-8 p-0'
              onClick={() => onChange({ pageIndex: pageIndex - 1, pageSize })}
              disabled={!canPreviousPage}
            >
              <span className='sr-only'>{texts.goToPreviousPage}</span>
              <NoddiIcon name='ChevronLeft' />
            </Button>
            <Button
              variant='outline'
              className='size-8 p-0'
              onClick={() => onChange({ pageIndex: pageIndex + 1, pageSize })}
              disabled={!canNextPage}
            >
              <span className='sr-only'>{texts.goToNextPage}</span>
              <NoddiIcon name='ChevronRight' />
            </Button>
            <Button
              variant='outline'
              className='size-8 p-0'
              onClick={() => onChange({ pageIndex: pageCount - 1, pageSize })}
              disabled={!canNextPage}
            >
              <span className='sr-only'>{texts.goToLastPage}</span>
              <NoddiIcon name='ChevronsRight' />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
